import type { ClubFeedItem, EventRsvpStatus } from "@/api/club/ClubApi";
import { useSetEventRsvp } from "@/api/club/useClub";
import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import { AppButton } from "@/ui/components/AppButton";
import { formatTimestampDE } from "@/utils/isoDate";
import React from "react";
import { Text, View } from "react-native";

const LABELS: Record<EventRsvpStatus, string> = {
  yes: "Zusage",
  maybe: "Vielleicht",
  no: "Absage",
};

export function EventRsvpPanel({
  item,
  organizationId,
  disabled,
}: {
  item: ClubFeedItem;
  organizationId?: string;
  disabled?: boolean;
}) {
  const { theme } = useTheme();
  const s = useStyles();
  const rsvp = useSetEventRsvp(organizationId);
  const cancelled = !!item.cancelled_at;
  const my = (item.my_rsvp as EventRsvpStatus | null | undefined) ?? null;
  const yesCount = item.yes_count ?? 0;
  const max = item.max_attendees ?? null;
  const deadline = item.rsvp_deadline
    ? new Date(item.rsvp_deadline)
    : null;
  const deadlinePassed = deadline ? deadline.getTime() < Date.now() : false;

  const hintParts: string[] = [];
  if (max != null) hintParts.push(`${yesCount} / ${max} Zusagen`);
  else if (yesCount > 0) hintParts.push(`${yesCount} Zusagen`);
  if (deadline) {
    hintParts.push(
      `Anmeldung bis ${formatTimestampDE(deadline)}`
    );
  }

  return (
    <View style={{ gap: theme.space.sm }}>
      <Text style={s.textH3}>Teilnahme</Text>
      {cancelled ? (
        <Text style={[s.textBody, { color: theme.color.danger }]}>
          Veranstaltung abgesagt
          {item.cancel_reason ? `: ${item.cancel_reason}` : ""}
        </Text>
      ) : null}
      {hintParts.length > 0 ? (
        <Text style={[s.textTiny, { color: theme.color.muted }]}>
          {hintParts.join(" · ")}
        </Text>
      ) : null}
      {deadlinePassed && !cancelled ? (
        <Text style={[s.textTiny, { color: theme.color.warning }]}>
          Anmeldefrist abgelaufen – Absage bleibt möglich.
        </Text>
      ) : null}
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space.sm }}>
        {(["yes", "maybe", "no"] as EventRsvpStatus[]).map((status) => (
          <AppButton
            key={status}
            variant={my === status ? "primary" : "outline"}
            selected={my === status}
            disabled={disabled || cancelled || rsvp.isPending}
            loading={rsvp.isPending && my !== status}
            onPress={() =>
              rsvp.mutate({
                eventItemId: item.item_id,
                status,
              })
            }
          >
            {LABELS[status]}
          </AppButton>
        ))}
      </View>
      {rsvp.isError ? (
        <Text style={[s.textTiny, { color: theme.color.danger }]}>
          {(rsvp.error as Error)?.message ?? "RSVP fehlgeschlagen"}
        </Text>
      ) : null}
    </View>
  );
}
